import React, { useState, useEffect } from 'react';
import { loginUser, registerUser, saveSearch, getFavorites, addFavorite, removeFavorite } from './utils/api';
import PreferenceForm from './components/PreferenceForm';
import ResultsDisplay from './components/ResultsDisplay';
import RadarChart from './components/RadarChart';
import SearchHistory from './components/SearchHistory';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import UserMenu from './components/UserMenu';
import FavoritesPanel from './components/FavoritesPanel';
import './App.css';

// ── Site-wide password gate ────────────────────────────────────────────────
// Change this string to update the site password, then rebuild + redeploy.
const SITE_PASSWORD = 'TasteApp2026';

function SitePasswordGate({ onUnlock }) {
  const [input, setInput] = useState('');
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  const attempt = () => {
    if (input === SITE_PASSWORD) {
      sessionStorage.setItem('site_unlocked', '1');
      onUnlock();
    } else {
      setError(true);
      setShake(true);
      setInput('');
      setTimeout(() => setShake(false), 500);
    }
  };

  return (
    <div className="gate-overlay">
      <div className={`gate-card ${shake ? 'gate-shake' : ''}`}>
        <h1 className="gate-title">JustTaste</h1>
        <p className="gate-subtitle">Enter the site password to continue</p>
        <input
          type="password"
          className={`gate-input ${error ? 'gate-input-error' : ''}`}
          placeholder="Password"
          value={input}
          autoFocus
          onChange={(e) => { setInput(e.target.value); setError(false); }}
          onKeyDown={(e) => e.key === 'Enter' && attempt()}
        />
        {error && <p className="gate-error">Incorrect password — try again</p>}
        <button className="gate-btn" onClick={attempt}>Enter</button>
      </div>
    </div>
  );
}

function App() {
  const [siteUnlocked, setSiteUnlocked] = useState(
    () => sessionStorage.getItem('site_unlocked') === '1'
  );
  const [currentUser, setCurrentUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [results, setResults] = useState([]);
  const [showRadarChart, setShowRadarChart] = useState(false);
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [currentPreferences, setCurrentPreferences] = useState(null);
  const [currentPositions, setCurrentPositions] = useState(null);
  const [currentExclusions, setCurrentExclusions] = useState(null);
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    // Check for saved user in localStorage
    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) {
      try {
        setCurrentUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('Error parsing saved user:', error);
      }
    }
  }, []);

  const handleLogin = async (username, password) => {
    try {
      const data = await loginUser(username, password);
      if (data.user) {
        setCurrentUser(data.user);
        localStorage.setItem('currentUser', JSON.stringify(data.user));
        setShowLogin(false);
        try {
          const favs = await getFavorites(data.user.username);
          setFavorites(favs || []);
        } catch (e) { /* ignore */ }
      }
    } catch (error) {
      throw error;
    }
  };

  const handleRegister = async (firstName, lastName, username, password) => {
    try {
      const data = await registerUser(firstName, lastName, username, password);
      if (data.user) {
        setCurrentUser(data.user);
        localStorage.setItem('currentUser', JSON.stringify(data.user));
        setShowRegister(false);
      }
    } catch (error) {
      throw error;
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    localStorage.removeItem('currentUser');
    setResults([]);
    setShowRadarChart(false);
    setFavorites([]);
  };

  const handleResults = (foodResults, _mode) => {
    setResults(foodResults);
    setShowRadarChart(false);
    // Smooth scroll to results section
    setTimeout(() => {
      const el = document.getElementById('results-section');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleSearchSubmitted = async (preferences, positions, exclusions, searchResults) => {
    setCurrentPreferences(preferences);
    setCurrentPositions(positions);
    setCurrentExclusions(exclusions);
    
    if (currentUser) {
      try {
        await saveSearch(currentUser.username, preferences, positions, exclusions, searchResults);
      } catch (error) {
        console.error('Error saving search:', error);
      }
    }
  };

  const handleApplySearch = (search) => {
    // This would apply the search settings to the form
    // For now, just close the search history
    console.log('Applying search:', search);
    // TODO: Implement search application
  };

  const handleShowEditProfile = () => {
    alert('Edit Profile functionality will be implemented next');
  };

  if (!siteUnlocked) {
    return <SitePasswordGate onUnlock={() => setSiteUnlocked(true)} />;
  }

  return (
    <div className="App">
      {/* Authentication UI */}
      <div className="auth-container">
        {currentUser ? (
          <UserMenu
            currentUser={currentUser}
            onLogout={handleLogout}
            onShowSearchHistory={() => setShowSearchHistory(true)}
            onShowEditProfile={handleShowEditProfile}
            onShowFavorites={() => setShowFavorites(true)}
          />
        ) : (
          <button className="login-btn" onClick={() => setShowLogin(true)}>
            Login
          </button>
        )}
      </div>

      {/* Login/Register Modals */}
      {showLogin && (
        <LoginForm
          onLogin={handleLogin}
          onClose={() => setShowLogin(false)}
          onSwitchToRegister={() => {
            setShowLogin(false);
            setShowRegister(true);
          }}
        />
      )}

      {showRegister && (
        <RegisterForm
          onRegister={handleRegister}
          onClose={() => setShowRegister(false)}
          onSwitchToLogin={() => {
            setShowRegister(false);
            setShowLogin(true);
          }}
        />
      )}

      {/* Search History */}
      {showSearchHistory && (
        <SearchHistory
          currentUser={currentUser}
          onApplySearch={handleApplySearch}
          isOpen={showSearchHistory}
          onClose={() => setShowSearchHistory(false)}
        />
      )}

      {showFavorites && (
        <FavoritesPanel
          isOpen={showFavorites}
          onClose={() => setShowFavorites(false)}
          favorites={favorites}
          onRemove={async (item) => {
            if (!currentUser) return;
            try {
              const res = await removeFavorite(currentUser.username, item);
              setFavorites(res.favorites || []);
            } catch (e) { /* ignore */ }
          }}
        />
      )}

      {/* Main Content */}
      <PreferenceForm
        currentUser={currentUser}
        onResults={handleResults}
        onSearchSubmitted={handleSearchSubmitted}
        onShowRadarChart={() => {
          if (results.length > 0 && currentPreferences) {
            setShowRadarChart(true);
          } else {
            alert('Please submit preferences first to see the radar chart');
          }
        }}
      />

      {results.length > 0 && (
        <>
          <div id="results-section">
          <ResultsDisplay
            results={results}
            onShowRadarChart={() => setShowRadarChart(true)}
            currentUser={currentUser}
            favorites={favorites}
            onToggleFavorite={async (item, isFav) => {
              if (!currentUser) return;
              try {
                const res = isFav
                  ? await removeFavorite(currentUser.username, item)
                  : await addFavorite(currentUser.username, item);
                setFavorites(res.favorites || []);
              } catch (e) { /* ignore */ }
            }}
          />
          </div>
          
          {showRadarChart && currentPreferences && currentPositions && currentExclusions && (
            <RadarChart
              preferences={currentPreferences}
              positions={currentPositions}
              exclusions={currentExclusions}
              results={results}
              onClose={() => setShowRadarChart(false)}
            />
          )}
        </>
      )}
    </div>
  );
}

export default App;
